
import Vue from 'vue';
import { CvRadioButton, CvRadioGroup } from '@carbon/vue';
import { ShowAIPreviewOption } from '@/constants/imageProcessingEngine';
import Close16 from '@carbon/icons-vue/es/close/16';
import { PREVIEW_PRODUCT_BOX } from '../../../constants';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { mapState, mapActions } from 'pinia';
import { ProductVariantImage } from '@midocean/webapps-shared-domain';
import { getScaledProductImageDPI } from '@/utils/areaHelper';
import { mm2px } from '@/utils';
import ArrowDown16 from '@carbon/icons-vue/es/arrow--down/16';
import ArrowUp16 from '@carbon/icons-vue/es/arrow--up/16';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import { base64StringToBlob } from 'blob-util';

export default Vue.extend({
  name: 'ProductPreviewModal',

  components: {
    Close16,
    ArrowDown16,
    ArrowUp16,
    CvRadioButton,
    CvRadioGroup,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
    preSelectedPositionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      PREVIEW_PRODUCT_BOX,
      positionId: this.preSelectedPositionId,
      displayImgsFrom: 0,
      product3dpreviewUrl: '',
      productAipreviewUrl: '',
      isProductPreviewUrlLoading: true,
      previewType: '2D',
      ShowAIPreviewOption,
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'selectedVariant',
      'unavailablePrintPositions',
      'getProductPrintPosition',
      'getProductName',
      'getProductSku',
      'getAIGeneratedPhotographyAvailable',
    ]),
    ...mapState(useShapeStore, ['positionShapes']),

    selectedPosition(): ProductVariantImage | undefined {
      return this.selectedVariant.images.find(
        (x) => x.printPositionID === this.positionId
      );
    },

    showPreviewUrl(): boolean {
      return (
        !this.isProductPreviewUrlLoading &&
        ((this.previewType === '2D' && this.product3dpreviewUrl !== '') ||
          (this.previewType === 'AI' && this.productAipreviewUrl !== ''))
      );
    },

    show2DPreview(): boolean {
      return (
        !this.isProductPreviewUrlLoading &&
        ((this.previewType === '2D' && this.product3dpreviewUrl === '') ||
          (this.previewType === 'AI' && this.productAipreviewUrl === ''))
      );
    },

    selectedVariantPositions(): {
      imageUrl: string;
      positionName: string;
      id: string;
    }[] {
      const productVariantImages = this.selectedVariant.images.filter((x) =>
        this.unavailablePrintPositions.some(
          (p) => p.positionId === x.printPositionID
        )
      );

      return productVariantImages.map((x) => {
        const position = this.unavailablePrintPositions.find(
          (p) => p.positionId === x.printPositionID
        );

        return {
          id: x.printPositionID,
          imageUrl: x.imageThumbnailUrl,
          positionName: position?.positionName || '',
        };
      });
    },

    activePositionShapes(): {
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      pngFileUrl: string | null;
      rotation: number;
    }[] {
      if (this.positionId) {
        const printPosition = this.getProductPrintPosition(this.positionId);

        if (printPosition) {
          const mirrorShapesFromPosition = this.getActiveMirrorShapes();
          const mirrorShapesFromPositionInArea = [] as AnnotationShape[];

          let selectedShapes = this.positionShapes(this.positionId).filter(
            (x) => x.isInArea
          ) as AnnotationShape[];

          mirrorShapesFromPosition.forEach((x) => {
            if (
              selectedShapes.findIndex((y) => y.id === x.originalShapeId) !== -1
            ) {
              mirrorShapesFromPositionInArea.push(x);
            }
          });

          selectedShapes = [
            ...selectedShapes,
            ...mirrorShapesFromPositionInArea,
          ];

          const mappedPositions = selectedShapes.map((x) => {
            const scaledDPI = getScaledProductImageDPI(
              printPosition.originalImageWidthPx,
              printPosition.originalImageHeightPx,
              printPosition.originalImageDpi,
              PREVIEW_PRODUCT_BOX
            );

            var variantImage = x.variantArtworkImages?.find(
              (v) => v.variantColor === this.selectedVariant.colour
            );

            const printArea = this.printPositionArea(printPosition.id, false);
            const isLanyard =
              printArea && printArea.kind === 'Rectangle'
                ? printArea.isLanyard
                : false;

            if (
              variantImage &&
              variantImage.widthPercentage > 0 &&
              variantImage.heightPercentage > 0 &&
              !isLanyard
            ) {
              let imageWidth = 0;
              let imageHeight = 0;
              if (
                printPosition.originalImageWidthPx >
                printPosition.originalImageHeightPx
              ) {
                imageWidth = PREVIEW_PRODUCT_BOX;
                imageHeight =
                  (imageWidth * printPosition.originalImageHeightPx) /
                  printPosition.originalImageWidthPx;
              } else {
                imageHeight = PREVIEW_PRODUCT_BOX;
                imageWidth =
                  (imageHeight * printPosition.originalImageWidthPx) /
                  printPosition.originalImageHeightPx;
              }

              return {
                id: x.id,
                x: variantImage.leftPercentage * imageWidth,
                y: variantImage.topPercentage * imageHeight,
                width: variantImage.widthPercentage * imageWidth,
                height: variantImage.heightPercentage * imageHeight,
                pngFileUrl: x.pngFile
                  ? URL.createObjectURL(x.pngFile as Blob)
                  : '',
                rotation: x.rotation,
              };
            }

            return {
              id: x.id,
              x: mm2px(x.xOnProductMM, scaledDPI),
              y: mm2px(x.yOnProductMM, scaledDPI),
              width: mm2px(x.widthOnProductMM, scaledDPI),
              height: mm2px(x.heightOnProductMM, scaledDPI),
              pngFileUrl: x.pngFile
                ? URL.createObjectURL(x.pngFile as Blob)
                : '',
              rotation: x.rotation,
            };
          });

          return mappedPositions;
        }
      }

      return [];
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.$modal.show('product-preview-modal');
      } else {
        this.$modal.hide('product-preview-modal');
      }
    },
  },

  async mounted() {
    if (this.isOpen) {
      this.$modal.show('product-preview-modal');
    }

    this.setIsLoaderActive(true);
    this.product3dpreviewUrl = await this.generateProduct3dpreviewUrl(
      this.positionId
    );
    this.setIsLoaderActive(false);
    this.isProductPreviewUrlLoading = false;
  },
  methods: {
    ...mapActions(useMirrorStore, ['getActiveMirrorShapes']),
    ...mapActions(useProductStore, ['printPositionArea']),
    ...mapActions(useGlobalStore, ['setIsLoaderActive']),
    ...mapActions(useShapeStore, [
      'generateProduct3dpreviewUrl',
      'generateProductAIpreviewUrl',
    ]),
    scrollImageNavigation(start: number) {
      this.displayImgsFrom = start;
    },

    async handlePreviewTypeChange(event: any) {
      const selectedValue = event;
      this.isProductPreviewUrlLoading = true;
      if (selectedValue === '2D' && this.product3dpreviewUrl === '') {
        this.setIsLoaderActive(true);
        this.product3dpreviewUrl = await this.generateProduct3dpreviewUrl(
          this.positionId
        );
        this.setIsLoaderActive(false);
      } else if (selectedValue === 'AI' && this.productAipreviewUrl === '') {
        this.setIsLoaderActive(true);
        this.productAipreviewUrl = await this.generateProductAIpreviewUrl(
          this.positionId
        );
        this.setIsLoaderActive(false);
      }
      this.isProductPreviewUrlLoading = false;
    },

    async handlePositionClick(positionId: string) {
      this.positionId = positionId;
      this.setIsLoaderActive(true);
      this.product3dpreviewUrl = await this.generateProduct3dpreviewUrl(
        positionId
      );
      this.setIsLoaderActive(false);
      this.$emit('position-selected', positionId);
    },
    isImageHidden(index: number) {
      let start = this.displayImgsFrom;
      let end = this.displayImgsFrom + 3;
      let imagesToDisplay = Array.from(
        { length: end - start + 1 },
        (_, i) => start + i
      );

      return !imagesToDisplay.includes(index);
    },
  },
});
